import {useMemo, useState} from "react";
import {CompanySettlementEntity} from "@ots-frontend/ots-data";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import {useDebouncedValue} from "@mantine/hooks";
import {getFormattedDatabaseCode} from "../../text/FormattedDatabaseCode/FormattedDatabaseCode";

export type UseCompanySettlementDataTableFilterProps = Omit<UseDataTableFilterProps<CompanySettlementEntity>, 'filterConfig'>

export function useCompanySettlementDataTableFilter(props: UseCompanySettlementDataTableFilterProps): UseDataTableFilterResult<CompanySettlementEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [companyQuery, setCompanyQuery] = useState<string | undefined>(undefined);
  const [companyDebouncedQuery] = useDebouncedValue(companyQuery, 200);

  const [statusQuery, setStatusQuery] = useState<string | undefined>(undefined);
  const [statusDebouncedQuery] = useDebouncedValue(statusQuery, 200);

  const [methodQuery, setMethodQuery] = useState<string | undefined>(undefined);
  const [methodDebouncedQuery] = useDebouncedValue(methodQuery, 200);

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined);
  const [nameDebouncedQuery] = useDebouncedValue(nameQuery, 200);

  const filterConfig: DataTableFilterConfig<CompanySettlementEntity> = useMemo(() => {
    return {
      "company": {
        setQuery: setCompanyQuery,
        query: companyQuery,
        debouncedQuery: companyDebouncedQuery,
        filter: (record, query) => !record.companyName || !record.companyName.toLowerCase().includes(query.toLowerCase())
      },
      "status": {
        setQuery: setStatusQuery,
        query: statusQuery,
        debouncedQuery: statusDebouncedQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.status, t)
          return !record.status.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
        }
      },
      "method": {
        setQuery: setMethodQuery,
        query: methodQuery,
        debouncedQuery: methodDebouncedQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.method, t)
          return !record.method || (!record.method.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase())))
        }
      },
      "name": {
        setQuery: setNameQuery,
        query: nameQuery,
        debouncedQuery: nameDebouncedQuery,
        filter: (record, query) => !record.name || !record.name.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    companyQuery,
    companyDebouncedQuery,
    statusQuery,
    statusDebouncedQuery,
    methodQuery,
    methodDebouncedQuery,
    nameQuery,
    nameDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
